import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import QRCode from 'qrcode'
import { sendOtp, verifyOtp, sendEmailOtp, verifyEmailOtp, getWechatQr, pollWechatLogin, setToken, passwordLogin } from '../api'

type LoginMethod = 'phone' | 'email' | 'wechat' | 'password'
type OtpStep = 'input' | 'code'

export default function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirect = searchParams.get('redirect') ?? '/admin'

  const [method, setMethod] = useState<LoginMethod>('phone')

  // Phone OTP state
  const [phone, setPhone] = useState('')
  const [phoneStep, setPhoneStep] = useState<OtpStep>('input')
  const [phoneCode, setPhoneCode] = useState('')
  const [phoneCountdown, setPhoneCountdown] = useState(0)

  // Email OTP state
  const [email, setEmail] = useState('')
  const [emailStep, setEmailStep] = useState<OtpStep>('input')
  const [emailCode, setEmailCode] = useState('')
  const [emailCountdown, setEmailCountdown] = useState(0)

  // WeChat state
  const [wechatState, setWechatState] = useState('')
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [wechatStatus, setWechatStatus] = useState<'idle' | 'loading' | 'scanning' | 'done' | 'expired'>('idle')
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const qrExpireRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Password state
  const [pwUsername, setPwUsername] = useState('')
  const [pwPassword, setPwPassword] = useState('')

  // Shared state
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Countdown ticker
  useEffect(() => {
    if (phoneCountdown <= 0) return
    const t = setInterval(() => setPhoneCountdown((n) => (n <= 1 ? (clearInterval(t), 0) : n - 1)), 1000)
    return () => clearInterval(t)
  }, [phoneCountdown])

  useEffect(() => {
    if (emailCountdown <= 0) return
    const t = setInterval(() => setEmailCountdown((n) => (n <= 1 ? (clearInterval(t), 0) : n - 1)), 1000)
    return () => clearInterval(t)
  }, [emailCountdown])

  // WeChat polling cleanup
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      if (qrExpireRef.current) clearTimeout(qrExpireRef.current)
    }
  }, [])

  // Listen for WeChat postMessage (from callback popup)
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.data?.type === 'wechat_login' && e.data?.state === wechatState) {
        // Token will be picked up by poll
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [wechatState])

  function switchMethod(m: LoginMethod) {
    setMethod(m)
    setError('')
    if (m === 'wechat' && wechatStatus === 'idle') {
      loadWechatQr()
    }
  }

  async function loadWechatQr() {
    setWechatStatus('loading')
    setError('')
    try {
      const { state, qrUrl } = await getWechatQr()
      setWechatState(state)
      const dataUrl = await QRCode.toDataURL(qrUrl, { width: 200, margin: 2, color: { dark: '#000', light: '#fff' } })
      setQrDataUrl(dataUrl)
      setWechatStatus('scanning')

      // Start polling every 2s
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = setInterval(async () => {
        try {
          const result = await pollWechatLogin(state)
          if (result.ok && result.token) {
            clearInterval(pollRef.current!)
            setToken(result.token)
            setWechatStatus('done')
            navigate(redirect, { replace: true })
          }
        } catch { /* ignore */ }
      }, 2000)

      // QR expires in 9 minutes
      if (qrExpireRef.current) clearTimeout(qrExpireRef.current)
      qrExpireRef.current = setTimeout(() => {
        clearInterval(pollRef.current!)
        setWechatStatus('expired')
      }, 9 * 60 * 1000)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '获取二维码失败')
      setWechatStatus('idle')
    }
  }

  // ── Phone OTP ──────────────────────────────────────────────

  async function handleSendPhoneOtp(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!/^1[3-9]\d{9}$/.test(phone)) { setError('请输入有效的中国大陆手机号'); return }
    setLoading(true)
    try {
      await sendOtp(phone)
      setPhoneStep('code')
      setPhoneCountdown(60)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '发送失败，请稍后重试')
    } finally { setLoading(false) }
  }

  async function handleVerifyPhoneOtp(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!/^\d{6}$/.test(phoneCode)) { setError('请输入 6 位验证码'); return }
    setLoading(true)
    try {
      await verifyOtp(phone, phoneCode)
      navigate(redirect, { replace: true })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '验证失败，请重试')
    } finally { setLoading(false) }
  }

  // ── Email OTP ──────────────────────────────────────────────

  async function handleSendEmailOtp(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError('请输入有效的邮箱地址'); return }
    setLoading(true)
    try {
      await sendEmailOtp(email)
      setEmailStep('code')
      setEmailCountdown(60)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '发送失败，请稍后重试')
    } finally { setLoading(false) }
  }

  async function handleVerifyEmailOtp(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!/^\d{6}$/.test(emailCode)) { setError('请输入 6 位验证码'); return }
    setLoading(true)
    try {
      await verifyEmailOtp(email, emailCode)
      navigate(redirect, { replace: true })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '验证失败，请重试')
    } finally { setLoading(false) }
  }

  // ── Password Login ─────────────────────────────────────────

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!pwUsername || !pwPassword) { setError('请输入用户名和密码'); return }
    setLoading(true)
    try {
      await passwordLogin(pwUsername, pwPassword)
      navigate(redirect, { replace: true })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '登录失败，请重试')
    } finally { setLoading(false) }
  }

  // ── Render ─────────────────────────────────────────────────

  const tabCls = (m: LoginMethod) =>
    `flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
      method === m ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-gray-200'
    }`

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-600 mb-4">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white">AIBens.CC</h1>
          <p className="text-gray-400 text-sm mt-1">内容发布管理平台</p>
        </div>

        <div className="card space-y-4">
          {/* Tab switcher */}
          <div className="flex gap-1 bg-gray-800/60 p-1 rounded-xl">
            <button className={tabCls('phone')} onClick={() => switchMethod('phone')}>手机号</button>
            <button className={tabCls('email')} onClick={() => switchMethod('email')}>邮箱</button>
            <button className={tabCls('wechat')} onClick={() => switchMethod('wechat')}>微信</button>
            <button className={tabCls('password')} onClick={() => switchMethod('password')}>密码</button>
          </div>

          {/* ── Phone OTP ── */}
          {method === 'phone' && (
            phoneStep === 'input' ? (
              <form onSubmit={handleSendPhoneOtp} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">手机号</label>
                  <div className="flex gap-2">
                    <span className="flex items-center px-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 text-sm select-none">+86</span>
                    <input type="tel" value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                      placeholder="13800138000" maxLength={11} className="input flex-1" autoFocus required />
                  </div>
                </div>
                {error && <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{error}</p>}
                <button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  发送验证码
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyPhoneOtp} className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-gray-300">验证码</label>
                    <button type="button" onClick={() => { setPhoneStep('input'); setPhoneCode(''); setError('') }}
                      className="text-xs text-gray-500 hover:text-gray-300 transition-colors">更换手机号</button>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">已发送 6 位验证码至 <span className="text-gray-300">+86 {phone}</span></p>
                  <input type="text" inputMode="numeric" value={phoneCode}
                    onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456" maxLength={6} className="input tracking-widest text-center text-xl" autoFocus required />
                </div>
                {error && <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{error}</p>}
                <button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  验证登录
                </button>
                <div className="text-center">
                  <button type="button" onClick={async () => { setError(''); setLoading(true); try { await sendOtp(phone); setPhoneCountdown(60) } catch (e: unknown) { setError(e instanceof Error ? e.message : '发送失败') } finally { setLoading(false) } }}
                    disabled={phoneCountdown > 0 || loading}
                    className="text-sm text-gray-500 hover:text-blue-400 disabled:hover:text-gray-500 transition-colors disabled:cursor-not-allowed">
                    {phoneCountdown > 0 ? `重新发送 (${phoneCountdown}s)` : '重新发送验证码'}
                  </button>
                </div>
              </form>
            )
          )}

          {/* ── Email OTP ── */}
          {method === 'email' && (
            emailStep === 'input' ? (
              <form onSubmit={handleSendEmailOtp} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">邮箱地址</label>
                  <input type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com" className="input" autoFocus required />
                </div>
                {error && <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{error}</p>}
                <button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  发送验证码
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyEmailOtp} className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-sm font-medium text-gray-300">验证码</label>
                    <button type="button" onClick={() => { setEmailStep('input'); setEmailCode(''); setError('') }}
                      className="text-xs text-gray-500 hover:text-gray-300 transition-colors">更换邮箱</button>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">已发送 6 位验证码至 <span className="text-gray-300">{email}</span></p>
                  <input type="text" inputMode="numeric" value={emailCode}
                    onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456" maxLength={6} className="input tracking-widest text-center text-xl" autoFocus required />
                </div>
                {error && <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{error}</p>}
                <button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                  验证登录
                </button>
                <div className="text-center">
                  <button type="button" onClick={async () => { setError(''); setLoading(true); try { await sendEmailOtp(email); setEmailCountdown(60) } catch (e: unknown) { setError(e instanceof Error ? e.message : '发送失败') } finally { setLoading(false) } }}
                    disabled={emailCountdown > 0 || loading}
                    className="text-sm text-gray-500 hover:text-blue-400 disabled:hover:text-gray-500 transition-colors disabled:cursor-not-allowed">
                    {emailCountdown > 0 ? `重新发送 (${emailCountdown}s)` : '重新发送验证码'}
                  </button>
                </div>
              </form>
            )
          )}

          {/* ── Password ── */}
          {method === 'password' && (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">用户名</label>
                <input type="text" value={pwUsername}
                  onChange={(e) => setPwUsername(e.target.value)}
                  placeholder="admin" className="input" autoFocus required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">密码</label>
                <input type="password" value={pwPassword}
                  onChange={(e) => setPwPassword(e.target.value)}
                  placeholder="••••••••" className="input" required />
              </div>
              {error && <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{error}</p>}
              <button type="submit" className="btn-primary w-full" disabled={loading}>
                {loading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                登录
              </button>
            </form>
          )}

          {/* ── WeChat QR ── */}
          {method === 'wechat' && (
            <div className="space-y-4">
              <div className="flex flex-col items-center gap-3 py-2">
                {wechatStatus === 'loading' && (
                  <div className="w-[200px] h-[200px] flex items-center justify-center">
                    <span className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
                {(wechatStatus === 'scanning' || wechatStatus === 'done') && qrDataUrl && (
                  <div className="p-3 bg-white rounded-xl">
                    <img src={qrDataUrl} alt="微信扫码登录" width={200} height={200} />
                  </div>
                )}
                {wechatStatus === 'expired' && (
                  <div className="w-[200px] h-[200px] bg-gray-800 rounded-xl flex flex-col items-center justify-center gap-2 border border-gray-700">
                    <span className="text-gray-400 text-sm">二维码已过期</span>
                    <button onClick={() => { setWechatStatus('idle'); loadWechatQr() }}
                      className="btn-secondary text-xs px-3 py-1.5">刷新二维码</button>
                  </div>
                )}
                {wechatStatus === 'scanning' && (
                  <p className="text-gray-400 text-sm text-center">使用微信扫描上方二维码登录</p>
                )}
                {wechatStatus === 'done' && (
                  <p className="text-green-400 text-sm">登录成功，正在跳转...</p>
                )}
              </div>
              {error && <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg px-3 py-2">{error}</p>}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-600 mt-6">
          仅限授权用户访问 · AIBens.CC
        </p>
      </div>
    </div>
  )
}
